import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    CircularProgress,
    Alert,
    Typography
} from '@mui/material';
import { StaffService } from '../api/services/staffService';

export default function EditAttendanceModal({ open, onClose, onSuccess, attendanceRecord }) {
    const [formData, setFormData] = useState({
        status: 'Present',
        clockInTime: '',
        clockOutTime: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    useEffect(() => {
        if (open && attendanceRecord) {
            setFormData({
                status: attendanceRecord.status || 'Present',
                // For datetime-local input, we need format YYYY-MM-DDTHH:mm
                clockInTime: attendanceRecord.clockInTime ? new Date(attendanceRecord.clockInTime).toISOString().slice(0, 16) : '',
                clockOutTime: attendanceRecord.clockOutTime ? new Date(attendanceRecord.clockOutTime).toISOString().slice(0, 16) : ''
            });
            setErrors({});
            setSubmitError('');
        }
    }, [open, attendanceRecord]);

    const validate = () => {
        const newErrors = {};
        if (!formData.status) newErrors.status = 'Status is required';
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        setIsSubmitting(true);
        setSubmitError('');

        try {
            const payload = {};
            
            // Only add fields if they changed compared to the original record
            if (formData.status !== attendanceRecord.status) {
                payload.status = formData.status;
            }
            
            const origClockIn = attendanceRecord.clockInTime ? new Date(attendanceRecord.clockInTime).toISOString().slice(0, 16) : '';
            if (formData.clockInTime !== origClockIn) {
                payload.clockInTime = formData.clockInTime ? new Date(formData.clockInTime).toISOString() : null;
            }

            const origClockOut = attendanceRecord.clockOutTime ? new Date(attendanceRecord.clockOutTime).toISOString().slice(0, 16) : '';
            if (formData.clockOutTime !== origClockOut) {
                payload.clockOutTime = formData.clockOutTime ? new Date(formData.clockOutTime).toISOString() : null;
            }

            // Only make API call if there are actual changes
            if (Object.keys(payload).length > 0) {
                await StaffService.updateAttendance(attendanceRecord.id || attendanceRecord._id, payload);
                onSuccess('Attendance record updated');
            }
            
            onClose();
        } catch (error) {
            setSubmitError(error.message || 'Failed to update attendance. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!attendanceRecord) return null;

    return (
        <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Attendance</DialogTitle>
            <DialogContent dividers>
                {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
                
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="text.secondary">Staff Member</Typography>
                    <Typography variant="body1" fontWeight="bold">
                        {attendanceRecord.staff?.fullName || attendanceRecord.staffName || attendanceRecord.staff || 'Unknown'}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>Date</Typography>
                    <Typography variant="body1">
                        {attendanceRecord.date || attendanceRecord.attendanceDate || '--'}
                    </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <FormControl required error={!!errors.status} disabled={isSubmitting}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={formData.status}
                            label="Status"
                            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        >
                            <MenuItem value="Present">Present</MenuItem>
                            <MenuItem value="Absent">Absent</MenuItem>
                            <MenuItem value="Late">Late</MenuItem>
                            <MenuItem value="Half-Day">Half-Day</MenuItem>
                        </Select>
                    </FormControl>

                    <TextField
                        type="datetime-local"
                        label="Clock In Time"
                        value={formData.clockInTime}
                        onChange={(e) => setFormData({ ...formData, clockInTime: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.clockInTime}
                        helperText={errors.clockInTime}
                        disabled={isSubmitting}
                    />

                    <TextField
                        type="datetime-local"
                        label="Clock Out Time"
                        value={formData.clockOutTime}
                        onChange={(e) => setFormData({ ...formData, clockOutTime: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        error={!!errors.clockOutTime}
                        helperText={errors.clockOutTime}
                        disabled={isSubmitting}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, pt: 1 }}>
                <Button onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                <Button 
                    variant="contained" 
                    onClick={handleSubmit} 
                    disabled={isSubmitting}
                    startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
